import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const columns = [
  { label: 'Customer Name', key: 'company_name' },
  { label: 'Company', key: 'company_id' },
  { label: 'WhatsApp', key: 'whatsapp_number' },
  { label: 'Phone', key: 'phone' },
  { label: 'PAN', key: 'pancard' },
  { label: 'GST Number', key: 'gst_number' },
  { label: 'Email', key: 'email' },
  { label: 'KYC Doc', key: 'kyc_document_url' },
  { label: 'Advance Balance', key: 'advance_balance' },
  { label: 'Estimated Incoming', key: 'estimated_incoming' },
];

interface CustomerType {
  id: number | string;
  company_name?: string;
  company_id?: number | string;
  estimated_incoming?: number | string;
  address?: string;
  pancard?: string;
  city?: string;
  state?: string;
  country?: string;
  gst_number?: string;
  gst_uin?: string;
  code?: string;
  cin?: string;
  email?: string;
  phone?: string;
  whatsapp_number?: string;
  number?: string;
  [key: string]: any;
}

interface CompanyType {
  id: number | string;
  company_name: string;
  [key: string]: any;
}

// Token decode helper for JWT
function decodeToken(token: string | undefined | null) {
  if (!token) return {};
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (_e) {
    return {};
  }
}

const Customer: React.FC = () => {
  const [customers, setCustomers] = useState<CustomerType[]>([]);
  const [companies, setCompanies] = useState<CompanyType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<number | string | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [role, setRole] = useState<string | null>(null);
  const [statementRows, setStatementRows] = useState<any[]>([]);
  const [userCompanyId, setUserCompanyId] = useState<string | null>(null);
  const [showQuotationsModal, setShowQuotationsModal] = useState(false);
  const [selectedCustomerQuotations, setSelectedCustomerQuotations] = useState<any[]>([]);
  const [selectedCustomerName, setSelectedCustomerName] = useState('');

  const [selectedCustomerDetails, setSelectedCustomerDetails] = useState<CustomerType | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const navigate = useNavigate();

  // Fetch customers and companies in parallel, and set role/userCompanyId
  useEffect(() => {
    // Decode token and extract companyId (userId or company_id)
    const token = localStorage.getItem('token');
    let companyIdFromToken: string | null = null;
    if (token) {
      const decoded: any = decodeToken(token);
      if (decoded && (decoded.userId || decoded.company_id)) {
        companyIdFromToken = String(decoded.userId || decoded.company_id);
      }
    }
    setUserCompanyId(companyIdFromToken);

    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const [customersRes, companiesRes, rowsRes] = await Promise.all([
          axios.get(`${API_URL}/auth/customers`),
          axios.get(`${API_URL}/auth/companies`),
          axios.get(`${API_URL}/auth/bank-statement-rows`),
        ]);
        setStatementRows(rowsRes.data || []);
        let customerList = Array.isArray(customersRes.data) ? customersRes.data : [];
        const activeWorkspaceId = localStorage.getItem("selected_company_id");
        const effectiveCompanyId = companyIdFromToken || (activeWorkspaceId && activeWorkspaceId !== "all" ? activeWorkspaceId : null);
        if (effectiveCompanyId) {
          const filtered = customerList.filter(
            (c: CustomerType) =>
              c.company_id !== undefined &&
              String(c.company_id) === String(effectiveCompanyId)
          );
          setCustomers(filtered.length > 0 ? filtered : customerList);
        } else {
          setCustomers(customerList);
        }
        const compList = Array.isArray(companiesRes.data) ? companiesRes.data : [];
        if (effectiveCompanyId) {
          setCompanies(compList.filter((c: any) => String(c.id) === String(effectiveCompanyId)));
        } else {
          setCompanies(compList);
        }
      } catch (err) {
        setError('Error loading customers or companies');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleAdd = () => {
    navigate('/addcustomer/?customer_id=new');
  };

  const handleEdit = (id: number | string) => {
    navigate(`/addcustomer/?customer_id=${id}`);
  };

  const handleDelete = async (id: number | string) => {
    setDeleteError('');
    const confirmed = window.confirm('Are you sure you want to delete this customer?');
    if (!confirmed) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API_URL}/auth/customer/${id}`);
      setCustomers((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      setDeleteError('Error deleting customer');
    } finally {
      setDeletingId(null);
    }
  };

  // Helper: company_id → company_name
  const getCompanyName = (company_id: number | string | undefined) => {
    if (!company_id) return <span className="text-gray-400">-</span>;
    const company = companies.find((c) => String(c.id) === String(company_id));
    return company ? company.company_name : <span className="text-gray-400">-</span>;
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Customers</h2>
        <button
          onClick={handleAdd}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
        >
          Add Customer
        </button>
      </div>
      {error && <div className="text-red-600 mb-2">{error}</div>}
      {deleteError && <div className="text-red-600 mb-2">{deleteError}</div>}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white shadow rounded">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="py-2 px-4 border-b bg-gray-100 text-left text-sm font-medium"
                >
                  {col.label}
                </th>
              ))}
              <th className="py-2 px-4 border-b bg-gray-100 text-left text-sm font-medium">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length + 1} className="text-center py-6">Loading...</td>
              </tr>
            ) : customers.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="text-center py-6">
                  No customers found.
                </td>
              </tr>
            ) : (
              customers.map((customer) => (
                <tr key={customer.id} className="hover:bg-gray-50 transition">
                  {columns.map((col) => {
                    // If column is "company_id", render the company name
                    if (col.key === 'company_id') {
                      return (
                        <td key={col.key} className="py-2 px-4 border-b text-sm">
                          {getCompanyName(customer.company_id)}
                        </td>
                      );
                    }
                    if (col.key === 'advance_balance') {
                      const advanceAmt = statementRows
                        .filter(r => String(r.mapped_customer_id) === String(customer.id) && r.is_advance)
                        .reduce((sum, r) => sum + Number(r.credit || 0), 0);
                      return (
                        <td key={col.key} className="py-2 px-4 border-b text-sm font-bold text-amber-600">
                          ₹{advanceAmt.toFixed(2)}
                        </td>
                      );
                    }
                    if (col.key === 'estimated_incoming') {
                      return (
                        <td key={col.key} className="py-2 px-4 border-b text-sm font-semibold text-blue-700">
                          ₹{customer.estimated_incoming !== undefined && customer.estimated_incoming !== null ? Number(customer.estimated_incoming).toFixed(2) : '0.00'}
                        </td>
                      );
                    }
                    if (col.key === 'kyc_document_url') {
                      const url = customer.kyc_document_url;
                      return (
                        <td key={col.key} className="py-2 px-4 border-b text-sm">
                          {url ? (
                            <a
                              href={url.startsWith('http') ? url : `${API_URL}${url.startsWith('/') ? '' : '/'}${url}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-indigo-600 hover:text-indigo-800 font-semibold"
                            >
                              📎 View KYC
                            </a>
                          ) : (
                            <span className="text-gray-400">Not Uploaded</span>
                          )}
                        </td>
                      );
                    }
                    return (
                      <td key={col.key} className="py-2 px-4 border-b text-sm">
                        {customer[col.key] || <span className="text-gray-400">-</span>}
                      </td>
                    );
                  })}
                  <td className="py-2 px-4 border-b">
                    <button
                      className="px-3 py-1 mr-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition text-xs font-semibold"
                      onClick={() => {
                        setSelectedCustomerDetails(customer);
                        setShowDetailsModal(true);
                      }}
                    >
                      More
                    </button>
                    <button
                      className="px-3 py-1 mr-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition text-xs"
                      onClick={() => handleEdit(customer.id)}
                      disabled={deletingId === customer.id}
                    >
                      Edit
                    </button>
                    <button
                      className={`px-3 py-1 mr-2 bg-red-600 text-white rounded hover:bg-red-700 transition text-xs ${
                        deletingId === customer.id ? 'opacity-60 cursor-not-allowed' : ''
                      }`}
                      onClick={() => handleDelete(customer.id)}
                      disabled={deletingId === customer.id}
                    >
                      {deletingId === customer.id ? 'Deleting...' : 'Delete'}
                    </button>
                    <button
                      className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition text-xs"
                      onClick={async () => {
                        setSelectedCustomerName(customer.company_name || 'Customer');
                        try {
                          const qRes = await axios.get(`${API_URL}/auth/quotations`);
                          const allQ = Array.isArray(qRes.data) ? qRes.data : [];
                          const custQ = allQ.filter((q: any) => String(q.Customer_id) === String(customer.id));
                          setSelectedCustomerQuotations(custQ);
                          setShowQuotationsModal(true);
                        } catch (err) {
                          alert("Failed to load customer quotations history.");
                        }
                      }}
                    >
                      History
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Quotations History Modal */}
      {showQuotationsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 animate__animated animate__fadeIn">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full border border-slate-200 shadow-xl relative">
            <button
              onClick={() => setShowQuotationsModal(false)}
              className="absolute right-4 top-4 text-slate-400 hover:text-slate-600 text-lg font-bold"
              style={{ background: 'none', border: 'none' }}
            >
              &times;
            </button>
            <h3 className="font-extrabold text-lg text-blue-900 mb-4">
              Quotation History & Projects: {selectedCustomerName}
            </h3>
            {selectedCustomerQuotations.length === 0 ? (
              <p className="text-gray-500 text-sm py-4">No quotations shared with this customer yet.</p>
            ) : (
              <div className="overflow-x-auto max-h-[60vh]">
                <table className="min-w-full text-left text-xs">
                  <thead className="bg-blue-50 text-[10px] font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                    <tr>
                      <th className="p-2.5">Quotation No</th>
                      <th className="p-2.5">Date</th>
                      <th className="p-2.5 text-right">Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {selectedCustomerQuotations.map((q: any) => (
                      <tr key={q.id} className="hover:bg-blue-50/5">
                        <td className="p-2.5 font-bold text-blue-800">{q.QuotationNo}</td>
                        <td className="p-2.5 text-gray-600">{new Date(q.Date).toLocaleDateString('en-GB')}</td>
                        <td className="p-2.5 text-right font-extrabold text-slate-800">
                          ₹{Number(q.Total_Amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex justify-end pt-4 border-t border-gray-100 mt-4">
              <button
                type="button"
                onClick={() => setShowQuotationsModal(false)}
                className="px-4 py-2 border rounded-xl text-gray-500 bg-gray-50 hover:bg-gray-100 text-xs font-bold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedCustomerDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 animate__animated animate__fadeIn">
          <div className="bg-white rounded-3xl p-8 max-w-lg w-full border border-slate-200 shadow-2xl relative max-h-[85vh] overflow-y-auto text-left">
            <button
              onClick={() => setShowDetailsModal(false)}
              className="absolute right-6 top-6 text-slate-400 hover:text-slate-600 text-2xl font-bold"
              style={{ background: 'none', border: 'none' }}
            >
              &times;
            </button>
            <h3 className="font-extrabold text-xl text-indigo-950 mb-6 border-b pb-3 flex items-center gap-2">
              👤 Customer Details
            </h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
              {[
                { label: 'Customer Name', value: selectedCustomerDetails.company_name, full: true },
                { label: 'Associated Company', value: getCompanyName(selectedCustomerDetails.company_id), full: true },
                { label: 'WhatsApp Number', value: selectedCustomerDetails.whatsapp_number },
                { label: 'Phone / Mobile', value: selectedCustomerDetails.phone || selectedCustomerDetails.number },
                { label: 'Address', value: selectedCustomerDetails.address, full: true },
                { label: 'PAN', value: selectedCustomerDetails.pancard },
                { label: 'City', value: selectedCustomerDetails.city },
                { label: 'State', value: selectedCustomerDetails.state },
                { label: 'Country', value: selectedCustomerDetails.country },
                { label: 'GST Number', value: selectedCustomerDetails.gst_number },
                { label: 'GST UIN', value: selectedCustomerDetails.gst_uin },
                { label: 'Code', value: selectedCustomerDetails.code },
                { label: 'CIN', value: selectedCustomerDetails.cin },
                { label: 'Email', value: selectedCustomerDetails.email },
                { 
                  label: 'Advance Balance (To Be Billed)', 
                  value: (() => {
                    const advanceAmt = statementRows
                      .filter(r => String(r.mapped_customer_id) === String(selectedCustomerDetails.id) && r.is_advance)
                      .reduce((sum, r) => sum + Number(r.credit || 0), 0);
                    return `₹ ${advanceAmt.toFixed(2)}`;
                  })(),
                  full: true
                },
                { label: 'Estimated Incoming', value: selectedCustomerDetails.estimated_incoming !== undefined && selectedCustomerDetails.estimated_incoming !== null ? `₹ ${Number(selectedCustomerDetails.estimated_incoming).toFixed(2)}` : '-' },
              ].map((item, idx) => (
                <div key={idx} className={`${item.full ? 'col-span-2' : ''} border-b border-slate-100 pb-2`}>
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">{item.label}</label>
                  <span className="text-slate-800 font-semibold break-all">{item.value || <span className="text-slate-300 font-normal">-</span>}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-end pt-6">
              <button
                type="button"
                onClick={() => setShowDetailsModal(false)}
                className="px-6 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold uppercase tracking-wider transition shadow-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customer;